import { type ChangeEvent, useRef, useState } from "react";
import {
  Box,
  Text,
  UserBlock,
  LabelWrapper,
  TextField,
} from "@cruk/cruk-react-components";

import { calcLength } from "@fwa/src/utils/formUtils";
import { useTracking } from "@fwa/src/hooks/useTracking";
import { useBrandContext } from "@fwa/src/contexts/BrandContext";
import {
  DISPLAY_NAME_MAX_LENGTH,
  validateDisplayName,
} from "@fwa/src/validation/page";

import Editable from "@fwa/src/components/Editable";

import styles from "@fwa/src/components/EditableDisplayNameForm/styles.module.css";

import { type PageType } from "@fwa/src/types";

export function EditableDisplayNameForm({
  avatarUrl,
  canEdit,
  editButtonColor,
  fieldName,
  fullWidth = true,
  handleEditData,
  page,
  userName,
}: {
  avatarUrl: string;
  canEdit: boolean;
  editButtonColor?: string;
  fieldName: string;
  fullWidth?: boolean;
  handleEditData: (
    data: Record<string, string | object>,
    refresh?: boolean,
  ) => Promise<void | PageType>;
  page: PageType;
  userName: string;
}) {
  const ownerDisplayName = page?.owner?.displayName || "";
  const ownerJoinedName = [page?.owner?.forename, page?.owner?.surname]
    .filter(Boolean)
    .join(" ");

  const initialValue = ownerDisplayName || ownerJoinedName;
  const textRef = useRef<HTMLInputElement>(null);

  const { trackError } = useTracking();
  const [brandName] = useBrandContext();
  const [validationMessage, setValidationMessage] = useState<string>("");
  const [charCount, setCharCount] = useState(calcLength(initialValue));
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (): Promise<PageType | void> => {
    if (!textRef.current) return;
    const validationMessageString = validateDisplayName(
      textRef?.current?.value,
    );
    setValidationMessage(validationMessageString || "");
    if (validationMessageString) {
      return;
    } else {
      setIsLoading(true);
      return handleEditData(
        {
          owner: {
            displayName: textRef?.current?.value,
          },
        },
        false,
      )
        .then((pageData) => {
          setIsLoading(false);
          return pageData;
        })
        .catch((error) => {
          setValidationMessage("Unable to submit");
          setIsLoading(false);
          trackError(error as Error, { component: "EditableDisplayNameForm" });
          return;
        });
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target as HTMLInputElement;
    setCharCount(calcLength(input.value));
  };

  return canEdit ? (
    <Editable
      fullWidth={fullWidth}
      editNode={
        <Box>
          <TextField
            name={`${page.fundraiser.forename} ${page.fundraiser.surname}`}
            ref={textRef}
            label="Display name"
            defaultValue={initialValue}
            onChange={handleChange}
            errorMessage={validationMessage}
            hintText="(Optional) Use if you want to display something other than your account name"
            maxLength={DISPLAY_NAME_MAX_LENGTH}
          />

          <Text textAlign="right" marginTop="xxs">{`${
            DISPLAY_NAME_MAX_LENGTH - charCount
          } characters remaining`}</Text>

          <LabelWrapper label="Account name">
            <div className={styles["disabled-text-border"]}>
              <Text>{`${page.fundraiser.forename} ${page.fundraiser.surname}`}</Text>
            </div>
          </LabelWrapper>
        </Box>
      }
      viewNode={
        <UserBlock
          name={page.owner?.displayName ? page.owner.displayName : userName}
          avatarUrl={avatarUrl}
          themeName={brandName}
        />
      }
      handleSubmit={handleSubmit}
      isLoading={isLoading}
      editButtonColor={editButtonColor}
      editButtonPosition="center"
      tooltip="Edit page user display name"
      fieldName={fieldName}
    />
  ) : (
    <UserBlock
      name={page.owner?.displayName ? page.owner.displayName : userName}
      avatarUrl={avatarUrl}
      themeName={brandName}
    />
  );
}

export default EditableDisplayNameForm;
